import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonItemDivider,
  IonLabel,
  IonPage,
  IonRow,
  IonToolbar,
} from '@ionic/react'
import HeaderNav from '../../../components/headerNav/HeaderNav.component'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { getExposureOptionsSelector } from '../../options/selectors/exposureOptions.selector'
import { AccordianComponent } from '../../../components/accordian/Accordian.components'
import { IOption, IPNNVOption } from '../../../common/common.types'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import {
  resetExposure,
  saveExposure,
  setExposedTo,
  setExposureEquipment,
  setExposureList,
  setExposureType,
  setExposureTypePNNV,
  setSuspectedExposure,
  setSuspectedExposurePNNV,
} from '../store/exposure.actions'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { getExposureSelector } from '../selectors/getExposure.selector'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../utilities/object.util'
import { initialExposure } from '../store/exposure.reducers'
import './Exposure.page.css'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { IAddExposure } from '../store/exposure.types'
import { getProcedureStateSelector } from '../../../components/procedure/selectors'

const Exposure: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const exposureOptions = useSelector(getExposureOptionsSelector)
  const exposureState = useSelector(getExposureSelector)
  const procedure = useSelector(getProcedureStateSelector)

  const {
    exposureEquipmentOptions,
    exposureTypeOptions,
    exposedToOptions,
    exposureCrewOptions,
    suspectedExposureOptions,
  } = exposureOptions
  const { exposureList, crewList } = exposureState
  const { assessedByOptions } = procedure

  const onBackClick = () => {
    dispatch(setExposureList(exposureList))
    history.push(TabConstants.CHART_ROUTE)
  }

  const isExposureListEqual = (expList: IAddExposure) => {
    const fieldsToFilter = ['crewId']
    const currentState = filterOutObjectKeys(expList, fieldsToFilter)
    const initialState = filterOutObjectKeys(initialExposure, fieldsToFilter)
    return compareObjectsIfEqual(currentState, initialState)
  }

  const isClearAllDisabled = () => {
    return exposureList.length === 0 || exposureList.every((expList) => isExposureListEqual(expList))
  }

  const resetExposureState = () => {
    dispatch(resetExposure())
    dispatch(saveExposure())
  }

  const checkIfChecked = (id: number | undefined) => {
    return exposureList.some((expList) => !isExposureListEqual(expList) && expList.crewId === id)
  }

  const crewExposureMetadata = (crew: IOption) => {
    const selectedExposure = exposureList.find((exp) => exp.crewId === crew.id)
    const crewIndex = assessedByOptions.findIndex((option) => option.id === crew.id)
    const notExposureDefaultCrew = exposureCrewOptions.every((option) => option.id !== crew.id)

    return (
      <>
        {crewIndex === -1 && notExposureDefaultCrew && (
          <IonRow style={{ paddingLeft: '1.2rem' }}>
            <IonCol>
              <IonLabel color='danger'>{t('exposure.crewError')}</IonLabel>
            </IonCol>
          </IonRow>
        )}
        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='ExposureEquipment'
              listHeader={t('exposure.protectiveEquipment')}
              options={exposureEquipmentOptions}
              savedValues={selectedExposure?.exposureEquipment.length ? selectedExposure?.exposureEquipment : []}
              action={setExposureEquipment}
              dispatchData={crew.id}
              callback={saveExposure}
              label={t('exposure.protectiveEquipment')}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='SuspectedExposure'
              selectedOption={selectedExposure?.suspectedExposure}
              listHeader={t('exposure.suspectedExposure')}
              options={suspectedExposureOptions}
              action={(option: IOption) => dispatch(setSuspectedExposure(option, crew.id))}
              dispatchData={crew.id}
              label={t('exposure.suspectedExposure')}
              selectedPNNVOption={selectedExposure?.suspectedExposurePNNV}
              pnnvAction={(option: IPNNVOption) => dispatch(setSuspectedExposurePNNV(option, crew.id))}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='ExposureType'
              listHeader={t('exposure.exposureType')}
              options={exposureTypeOptions}
              savedValues={selectedExposure?.exposureType.length ? selectedExposure?.exposureType : []}
              action={setExposureType}
              callback={saveExposure}
              dispatchData={crew.id}
              label={t('exposure.exposureType')}
              savedPNNVValue={selectedExposure?.exposureTypePNNV}
              pnnvAction={setExposureTypePNNV}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='ExposedTo'
              selectedOption={selectedExposure?.exposedTo}
              listHeader={t('exposure.exposedTo')}
              options={exposedToOptions}
              action={(option: IOption) => dispatch(setExposedTo(option, crew.id))}
              dispatchData={crew.id}
              label={t('exposure.exposedTo')}
            />
          </IonCol>
        </IonRow>
      </>
    )
  }

  return (
    <IonPage id='ExposurePage'>
      <PCRHeader />
      <HeaderNav
        prevOnClick={onBackClick}
        prevLabel={t('common.buttons.Back')}
        isBackButton={true}
        nextLabel={t('common.buttons.Save')}
        title={t('fullChart.dispatch.Exposure')}
      />
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('fullChart.dispatch.ExposureDetailsProtectiveEquipment')}
        </IonLabel>
      </IonItemDivider>
      <IonContent color='light'>
        {crewList?.map((crew) => (
          <IonRow key={crew?.name}>
            <IonCol className='ion-no-padding'>
              <AccordianComponent
                dataTestId={`${crew.id}-${crew.name}`}
                heading={crew.name}
                content={crewExposureMetadata(crew)}
                isCheckMark={checkIfChecked(crew?.id)}
              />
            </IonCol>
          </IonRow>
        ))}
      </IonContent>
      <IonFooter>
        <IonToolbar color='light' className='p-10 '>
          <IonButtons className='btn-grps'>
            <IonButton
              className='btn btn-tablet'
              color='danger'
              fill='outline'
              expand='block'
              disabled={isClearAllDisabled()}
              onClick={() =>
                dispatch(
                  showAlert({
                    header: '',
                    message: 'Are you sure you want to clear all existing exposure data?',
                    buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: resetExposureState }],
                  })
                )
              }>
              {t('common.buttons.ClearAll')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  )
}

export default Exposure
